// ═══════════════════════════════════════════════════════════════════════════════
//  PANTALLA 5 — Motor de Calibración
//  docs/onboarding-master-v2.md, Parte I §5 + Parte II completa.
//
//  Responsabilidades:
//    1. Copy de transición y de transparencia (Parte I §0 y §5.1).
//    2. Orquestar el branching adaptativo del banco de 25 preguntas
//       (la lógica vive en ../calibrationScoring.js, aquí solo se conduce).
//    3. Temporizador suave por pregunta — "genera ritmo de juego, no ansiedad
//       de examen" (Parte I §5.2). Agotarlo NO castiga: cuenta como fallo pero
//       nunca interrumpe con un modal ni con lenguaje de examen.
//    4. Micro-feedback suave por slide (Parte II §3).
//    5. Entregar el resultado al orquestador para el contraste de §5.5.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCalibrationState,
  registerAnswer,
  getCurrentSlide,
  buildCalibrationResult,
} from '../calibrationScoring';
import { START_LEVEL } from '../calibrationBank';
import {
  CALIBRATION_INTRO,
  TRANSPARENCY_COPY,
  FEEDBACK_CORRECT,
  FEEDBACK_INCORRECT,
} from '../onboardingContent';
import { cancelSpeech } from '../onboardingSpeech';
import { etiquetaDePaso } from '../pasos';
// El sonido de acierto/fallo no puede salir del delegador de clics de la
// pagina: ahi solo se sabe QUE se toco, no si estuvo bien. Lo dispara el motor,
// que es el unico que conoce la respuesta.
import { acierto, fallo, seAcaba, arranca } from '../sonidos';

import SlideOpcionMultiple from './SlideOpcionMultiple';
import SlideOrdenaFrase from './SlideOrdenaFrase';
import SlideEscuchaComprende from './SlideEscuchaComprende';
import SlideEscuchaVoces from './SlideEscuchaVoces';
import SlideEncuentraError from './SlideEncuentraError';

// Tiempo límite suave por pregunta — Parte I §5.2: "8-10 seg".
// Los formatos con audio arrancan su cuenta solo tras escuchar, así que se les
// da el tope alto del rango; el resto usa el bajo.
const SOFT_LIMIT_MS = 10000;
const FEEDBACK_MS = 1400;

const SLIDE_COMPONENTS = {
  choice: SlideOpcionMultiple,
  order: SlideOrdenaFrase,
  listen: SlideEscuchaComprende,
  tone: SlideEscuchaVoces,
  error: SlideEncuentraError,
};

const FORMAT_LABEL = {
  choice: '✅ Opción múltiple',
  order: '🔤 Ordena la frase',
  listen: '🎧 Escucha y comprende',
  tone: '🗣️ Escucha y elige',
  error: '🐛 Encuentra el error',
};

function pick(list) {
  return list[Math.floor(Math.random() * list.length)];
}

export default function CalibrationEngine({ onComplete }) {
  const [started, setStarted] = useState(false);
  const [engine, setEngine] = useState(() => createCalibrationState(START_LEVEL));
  const [feedback, setFeedback] = useState(null);   // { correct, text }
  const [timeLeft, setTimeLeft] = useState(SOFT_LIMIT_MS);

  const slide = getCurrentSlide(engine);
  const answeredRef = useRef(false);

  /* ── Avance del motor tras cada respuesta ──────────────────────────────── */

  const handleAnswer = useCallback(
    (isCorrect, porTiempo = false) => {
      if (answeredRef.current) return;
      answeredRef.current = true;

      if (isCorrect) acierto();
      else if (porTiempo) seAcaba();
      else fallo();

      setFeedback({
        correct: isCorrect,
        text: isCorrect ? pick(FEEDBACK_CORRECT) : pick(FEEDBACK_INCORRECT),
      });

      // Pausa breve para que el alumno vea el feedback antes de avanzar.
      setTimeout(() => {
        setEngine((prev) => {
          const next = registerAnswer(prev, isCorrect);
          if (next.finished) {
            cancelSpeech();
            onComplete(buildCalibrationResult(next));
          }
          return next;
        });
        setFeedback(null);
        setTimeLeft(SOFT_LIMIT_MS);
        answeredRef.current = false;
      }, FEEDBACK_MS);
    },
    [onComplete]
  );

  /* ── Temporizador suave ────────────────────────────────────────────────── */

  useEffect(() => {
    if (!started || !slide || feedback) return;

    answeredRef.current = false;
    setTimeLeft(SOFT_LIMIT_MS);

    const tick = 250;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        const next = t - tick;
        if (next <= 0) {
          clearInterval(id);
          // Se acabó el tiempo: cuenta como no acertada, sin drama ni modal.
          handleAnswer(false, true);
          return 0;
        }
        return next;
      });
    }, tick);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, slide?.id, feedback]);

  useEffect(() => () => cancelSpeech(), []);

  /* ── Pantalla de transición (Parte I §5.1 + copy de transparencia §0) ──── */

  if (!started) {
    return (
      <div className="ob-screen">
        <p className="ob-eyebrow">{`${etiquetaDePaso(7)} · Calibración`}</p>
        <h1 className="ob-headline">
          <span className="ob-headline-linea">{CALIBRATION_INTRO.linea}</span>
          <span className="ob-bloque">
            {CALIBRATION_INTRO.bloque}
            <span className="ob-cambridge-highlight">{CALIBRATION_INTRO.bloqueAmarillo}</span>
          </span>
          <br />
          <span className="ob-headline-linea">{CALIBRATION_INTRO.linea2}</span>
        </h1>
        <p className="ob-honesty">{TRANSPARENCY_COPY}</p>
        <div className="ob-actions">
          <button
            type="button"
            className="ob-btn"
            data-sonido="propio"
            onClick={() => { arranca(); setStarted(true); }}
          >
            Empezar la calibración →
          </button>
        </div>
      </div>
    );
  }

  if (!slide) return null;

  const SlideComponent = SLIDE_COMPONENTS[slide.format];
  const timerPct = Math.max(0, (timeLeft / SOFT_LIMIT_MS) * 100);

  return (
    <div className="ob-screen">
      <div className="ob-slide-meta">
        <span className="ob-level-chip">{engine.level}</span>
        <span>{FORMAT_LABEL[slide.format]}</span>
      </div>

      <div className="ob-timer" aria-hidden="true">
        <div className="ob-timer-fill" style={{ width: `${timerPct}%` }} />
      </div>

      <SlideComponent
        key={slide.id}
        slide={slide}
        onAnswer={handleAnswer}
        locked={Boolean(feedback)}
      />

      {feedback && (
        <div className={`ob-feedback ${feedback.correct ? 'ob-feedback-good' : 'ob-feedback-soft'}`}>
          {feedback.text}
        </div>
      )}
    </div>
  );
}
